import React from 'react';
import type { FC } from 'react';
import { SiteAuthProvider } from '@wix/cli-auth';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { useAsync } from '@wix/cli-ui-kit';
import { match } from 'variant';
import { join } from 'node:path';
import { pathExists } from '@wix/cli-fs';
import { TRANSLATIONS_FILE_PATH } from '@wix/cli-astro-definitions';
import { useProjectModel } from '../../model.js';
import { TranslationPush } from './TranslationPush.js';
import { readTranslationsFile } from './read-translations-file.js';

export const TranslationPushCommand: FC<{
  onComplete?: () => void;
  skipConfirmation?: boolean;
}> = ({ onComplete, skipConfirmation }) => {
  const {
    model: { config, projectFolder },
  } = useProjectModel();

  const translationsFilePath = join(projectFolder, TRANSLATIONS_FILE_PATH);

  const { status } = useAsync(async () => {
    if (config.projectType !== 'Site') {
      throw new CliError({
        code: CliErrorCode.TranslationUnsupportedForProjectType(),
        cause: null,
      });
    }

    if (!(await pathExists(translationsFilePath))) {
      throw new CliError({
        code: CliErrorCode.TranslationFileNotFound({
          filePath: TRANSLATIONS_FILE_PATH,
        }),
        cause: null,
      });
    }

    const translations = await readTranslationsFile(translationsFilePath);

    return { siteId: config.siteId, translations };
  }, [translationsFilePath]);

  return match(status, {
    Loading: () => null,
    Error: () => null,
    Success: ({ result: { siteId, translations } }) => {
      return (
        <SiteAuthProvider siteId={siteId}>
          <TranslationPush
            translations={translations}
            onComplete={onComplete}
            skipConfirmation={skipConfirmation}
          />
        </SiteAuthProvider>
      );
    },
  });
};
